/**
 * @file SSOAttributeValueDropdown.js
 * @description A dropdown component for selecting SSO attribute values.
 */

import { useState, useEffect, useMemo } from 'react';
import { fetchSSOAttributeValues } from '../../../services/ssoService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

/**
 * Represents a dropdown component for selecting SSO attribute values.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {string} [props.selectedAttributeValue] - The ID of the selected SSO attribute value.
 * @param {function} props.onAttributeValueChange - The callback function to be called when the selected attribute value changes.
 * @param {string} [props.label="SSO Attribute Value"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.allowNullSelection=false] - Determines whether a null selection is allowed.
 * @param {boolean} [props.addLiteral=false] - Determines whether to add a "Literal" option to the dropdown.
 * @returns {JSX.Element} The rendered SSOAttributeValueDropdown component.
 */
const SSOAttributeValueDropdown = ({
    className,
    inlineLabel = true,
    selectedAttributeValue,
    onAttributeValueChange,
    label = 'SSO Attribute Value',
    isRequired = false,
    allowNullSelection = false,
    addLiteral = false,
    isDisabled = false,
    excludedValues = new Set(),
}) => {
    const [attributeValues, setAttributeValues] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getAttributeValues = async () => {
            setIsLoading(true);
            try {
                const ssoAttributeValuesResponse =
                    await fetchSSOAttributeValues();
                setAttributeValues(ssoAttributeValuesResponse);
            } catch (error) {
                console.error('Could not fetch SSO attribute values:', error);
                Toast.error(getText('msg_362a7acd8ecf'));
            } finally {
                setIsLoading(false);
            }
        };

        getAttributeValues();
    }, []);

    const getDefaultLabel = () => {
        if (selectedAttributeValue === 'literal') return 'Literal';
        const matchingAttributeValue = attributeValues.find(
            value => value.id === selectedAttributeValue,
        );
        return matchingAttributeValue
            ? matchingAttributeValue.name
            : 'Select SSO Attribute Value';
    };

    const attributeValueOptions = useMemo(() => {
        const nullOption = allowNullSelection
            ? [{ value: null, label: getText('msg_a2f2b2129ce5') }]
            : [];

        const mappedValues = attributeValues
            .filter(value => !excludedValues.has(value.id))
            .map(value => ({ value: value.id, label: value.name }));

        const literalOption = addLiteral
            ? [{ value: 'literal', label: getText('msg_92e97e035f44') }]
            : [];

        const sortedOptions = [...mappedValues, ...literalOption].sort((a, b) =>
            a.label.localeCompare(b.label),
        );

        return [...nullOption, ...sortedOptions];
    }, [attributeValues, allowNullSelection, addLiteral, excludedValues]);

    return (
        <div
            className={`form-group mb-3 col-12 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="sso-attribute-value-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="sso-attribute-value-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={attributeValueOptions}
                    value={selectedAttributeValue}
                    onChange={onAttributeValueChange}
                    disabled={
                        attributeValues.length === 0 || isLoading || isDisabled
                    }
                />
            </LoadingWrapper>
        </div>
    );
};

export default SSOAttributeValueDropdown;
